import { Component, signal } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService } from 'src/app/infrastructure/services/api-service/api.service';
import { DomainPageService } from '../../services/domain-page.service';
import { IDomainUserListData } from 'src/app/features/domain-user/models/domain-user-list-data';
import { LIST_KIND_NAME, _ListService } from 'src/app/infrastructure/services/list-services/_list.service';
import { requestName } from 'src/assets/common/enums/request-names.g';
import { DomainUserListService } from 'src/app/features/domain-user/services/domain-user-list.service';
import { _PageService } from 'src/app/infrastructure/services/page-services/_page.service';
import { SharedModule } from 'src/app/features/shared/shared.module';
import { _NavigationList_Base_ToolComponent } from 'src/app/infrastructure/components/tablist-components/_navigation-list-base-tool.component';
import { tabName } from 'src/app/infrastructure/services/page-services/models/tab-name';
import { _NavigationList_Base_FeatureComponent } from 'src/app/infrastructure/components/tablist-components/_navigation-list-base-feature.component';
import { TabDataService } from 'src/app/features/shared/central-page/services/tab-data.service';
import { PagerService } from 'src/app/features/shared/pager/pager.service';
import { IDomainUserListDto } from 'src/assets/common/models/domain-user-list-dto.g';
import { FilterService } from 'src/app/features/shared/filter/filter.service';
import { NavigationList_ToolComponent } from 'src/app/features/shared/lists/navigation-list/container/navigation-list-tool/navigation-list.component';
import { claimPage } from 'src/app/infrastructure/models/claim-page';
import { TabListService } from 'src/app/features/shared/central-page/services/tab-list.service';
import { TabListStorageService } from 'src/app/features/shared/storage/services/storage-services/tab-list-storage.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { unwrapData } from 'src/app/infrastructure/functions/unwrap-data.operator';
import { domainUserListData } from 'src/app/features/domain-user/models/domain-user-list-data';
import { ImageBoxTabListComponent } from 'src/app/features/shared/image-boxes/image-box-tab-list/image-box-tab-list.component';
import { ILevelGeneralListData } from 'src/app/infrastructure/models/level-general-list-data';
import { AccountPageService } from 'src/app/features/user-account/services/account-page.service';
import { NotificationService } from 'src/app/features/shared/notification/notification.service';
import { routePage } from 'src/app/infrastructure/services/route-service/models/route-page';
import { inactiveSuffixText } from 'src/app/infrastructure/pipes/inactive-suffix.pipe';

@Component({
    selector: 'app-domain-tablist',
    templateUrl: './domain-tablist.component.html',
    styleUrls: [
        '../../../shared/central-page/central-page.scss',
        '../../../shared/central-page/container/tablist/tablist.scss',
    ],
    standalone: true,
    imports: [
        ImageBoxTabListComponent,
        NavigationList_ToolComponent,
        SharedModule
    ],
    providers: [
        {provide: LIST_KIND_NAME, useValue: tabName.domains},
        TabListStorageService,
        TabDataService,
        PagerService,
        {provide: FilterService, useExisting: PagerService},

        DomainPageService,
        {provide: _PageService, useExisting: DomainPageService},

        DomainUserListService,
        {provide: _ListService, useExisting: DomainUserListService},
    ],
})
export class DomainTablistComponent
    extends _NavigationList_Base_FeatureComponent<IDomainUserListDto, IDomainUserListData>
{
    private readonly __apiService = injectRequired(ApiService);
    private readonly __domainPageService = injectRequired(DomainPageService);
    private readonly __accountPageService = injectRequired(AccountPageService);
    private readonly __notificationService = injectRequired(NotificationService);

    protected override readonly _listService = injectRequired(_ListService);
    protected override readonly _pageService = injectRequired(_PageService);
    protected override readonly _pagerService = injectRequired(PagerService);
    protected override readonly _tabDataService = injectRequired(TabDataService);
    protected override readonly _tabListService = injectRequired(TabListService);

    override input_btnAddData$$ = this._input_btnAddData$$;
    override input_btnDeleteData$$ = this._input_btnDeleteData$$;
    override input_buttonsHide$$ = this._input_buttonsHide$$;
    override input_claimPageList$$ = this._input_claimPageList$$;
    override input_noClick$$ = this._input_noClick$$;
    override input_pageSize$$ = this._input_pageSize$$;

    override out_onClick = this._out_onClick;
    override out_onPageLoaded = this._out_onPageLoaded;
    override out_onSave = this._out_onSave;

    override btnAddData$$ = this._input_btnAddData$$;
    override btnDeleteData$$ = this._input_btnDeleteData$$;
    override buttonsHide$$ = this._input_buttonsHide$$;
    override httpParams$$ = this._computed_httpParams$$;
    override list$$ = this._computed_list$$;
    override noClick$$ = this._input_noClick$$;
    override pageSize$$ = this._input_pageSize$$;
    override requestName$$ = this._computed_requestName$$;
    override tabIndex$$ = this._computed_tabIndex$$;

    override claimPageList$$ = signal(claimPage.domains);

    override itemLines = 1;
    override hasImageBox = true;

    logoGuid = domainUserListData.domain_logoThumb_guid;
    domainNr = domainUserListData.nr;
    domainName = domainUserListData.domain_name;
    domainOwnerEmail = domainUserListData.domain_owner_email;
    twoFactorRequired = domainUserListData.twoFactorRequired;
    active = domainUserListData.active;
    // Marker achter de hele titel (nr - naam), zelfde regel als de FunctionMember-/ProjectMember-lijsten.
    protected readonly _inactiveSuffixText = inactiveSuffixText;

    override ngOnInit(): void {
        this._ngOnInit();
    }

    override emit_btnAddClick(): void {
        this.__domainPageService.pageCreate_goto();
    }

    override emit_onClick(item: ILevelGeneralListData): void {
        const __item = item as IDomainUserListData;

        // 2FA-beleid van het domain geldt en de gebruiker heeft geen 2FA: de backend weigert het domain
        // (TwoFactorRequired). Meteen naar Instellingen i.p.v. een mislukte navigatie.
        if (__item[domainUserListData.twoFactorRequired]) {
            this.__notificationService.pushNotification({
                severity: 'warning',
                title: 'Tweestapsverificatie vereist',
                text: 'Dit domain vereist tweestapsverificatie. Schakel die hier in om het domain weer te openen.',
            });
            this.__accountPageService.page_goto(routePage.settings);
            return;
        }

        this.__domainPageService.pageRoute_goto(__item);
    }

    override emit_onRestore(restoreList: any[]): void {
        this._restore$(restoreList, requestName.listRestore).subscribe();
    }

    override emit_onDeleteDeep(deleteList: any[]): void {
        this._deleteDeep$(deleteList).subscribe();
    }

    protected override _list_get$(): Observable<IDomainUserListDto[]> {
        return from(this.__apiService.GET('/api/DomainUser/list-from-user', {})).pipe(map(r => (r.data as any)?.list as IDomainUserListDto[] ?? []));
    }

    // Herstelde/verwijderde deze lijst betreft DomainUser-rijen (het eigen domain-lidmaatschap),
    // niet Domain-rijen — voorheen patchte dit naar /api/Domain/list-restore (verkeerde entiteit, en
    // dat endpoint bestond sowieso niet voor DomainUser), dus deze knop deed feitelijk niets.
    protected override _restore_patch$(restoreList: any[], httpParams: HttpParams | null): Observable<IDomainUserListDto> {
        return from(this.__apiService.PATCH('/api/DomainUser/list-restore', {
            body: restoreList,
        })).pipe(unwrapData<IDomainUserListDto>());
    }

    protected override _deleteDeep_delete$(deleteList: any[], httpParams: HttpParams | null): Observable<IDomainUserListDto> {
        return from(this.__apiService.PATCH('/api/DomainUser/list-delete-deep', {
            body: deleteList,
        })).pipe(unwrapData<IDomainUserListDto>());
    }

}
